import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import APP_BASE_URL, { IMG_BASE_URL } from "../../components/Api/globalApi";
import { toast } from "react-toastify";
import { CartContext } from "../../context/CartContext";
import { useContext } from "react";
import "./ShopProducts.css";

const ShopProducts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const shopId = params.get("shop_id");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const imageBaseUrl = IMG_BASE_URL;
  const { cartItems, addToCart, fetchCart } = useContext(CartContext);
  const pendingHandledRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate auto-add on re-render/StrictMode remount.
    if (pendingHandledRef.current) return;

    const pendingItemStr = localStorage.getItem("pendingCartItem");
    const token = localStorage.getItem("virtualmall_token");
    if (!pendingItemStr || !token) return;

    pendingHandledRef.current = true;

    let parsedProduct = null;
    try {
      parsedProduct = JSON.parse(pendingItemStr);
    } catch (e) {
      console.error("Invalid pendingCartItem payload", e);
      localStorage.removeItem("pendingCartItem");
      return;
    }

    // Remove first so repeated mounts don't keep incrementing quantity.
    localStorage.removeItem("pendingCartItem");

    const addPendingItem = async () => {
      const latestCart = await fetchCart();
      const alreadyInCart = (latestCart || []).some(
        (item) => Number(item.vsp_id) === Number(parsedProduct?.vsp_id)
      );

      if (alreadyInCart) {
        return;
      }

      const isAdded = await addToCart(parsedProduct);
      if (isAdded) {
        toast.success("Item added successfully to cart");
      } else {
        // Restore only when request actually fails.
        localStorage.setItem("pendingCartItem", JSON.stringify(parsedProduct));
      }
    };

    addPendingItem();
  }, [addToCart, fetchCart]);


  const handleAddToCart = async (product) => {
    const userId = localStorage.getItem("virtual_user_id") || localStorage.getItem("user_id");
    if (!userId) {
      localStorage.setItem("pendingCartItem", JSON.stringify(product));
      navigate(`/virtual_mall_login?redirect=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    const isAdded = await addToCart(product);
    if (isAdded) {
      toast.success(`${product.vsp_name} added to cart!`);
    } else {
      toast.error("Could not add to cart");
    }
  };

  useEffect(() => {
    if (!shopId) {
      setError("Missing shop_id");
      setLoading(false);
      return;
    }
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${APP_BASE_URL}/shop-products`, {
          params: { shop_id: shopId },
        });
        if (res.data?.status) {
          setProducts(res.data.data || []);
        } else {
          setError(res.data?.message || "Failed to load products");
        }
      } catch (err) {
        setError(err.message || "Failed to load products");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
    
  }, [shopId]);

  const goBack = () => navigate(-1);
  const uniqueCategories = [...new Set(products.map((p) => p.vsp_category?.toString()).filter(Boolean))];
  const categoryChips = [
    { value: "all", label: "All Products" },
    ...uniqueCategories.map((category) => ({
      value: category,
      label: `Category ${category}`,
    })),
  ];

  const filteredProducts = products.filter((p) => {
    const matchesCategory = filterCategory === "all" || p.vsp_category?.toString() === filterCategory;
    const matchesSearch = !searchQuery.trim() || 
      (p.vsp_name && p.vsp_name.toLowerCase().includes(searchQuery.toLowerCase().trim())) ||
      (p.vsp_description && p.vsp_description.toLowerCase().includes(searchQuery.toLowerCase().trim()));
    return matchesCategory && matchesSearch;
  });

  const activeFilterLabel =
    filterCategory === "all" ? "All Products" : `Category ${filterCategory}`;

  return (
    <div className="shop-products-page">
      <div className="shop-products-shell">
        <section className="shop-products-hero">
          <button onClick={goBack} className="shop-products-back-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back
          </button>
          <div className="shop-products-hero__content">
            <div className="shop-products-hero__text">
              <span className="shop-products-hero__eyebrow">Virtual Mall Catalog</span>
              <h1 className="shop-products-hero__title">Shop Products</h1>
              <p className="shop-products-hero__subtitle">
                Browse products by category and explore what this shop currently has available.
              </p>
            </div>
            <div className="shop-products-hero__meta">
              {shopId && <span className="shop-products-badge">Shop #{shopId}</span>}
              <div className="shop-products-stat-card">
                <span className="shop-products-stat-card__label">Products</span>
                <strong className="shop-products-stat-card__value">{products.length}</strong>
              </div>
              <div className="shop-products-stat-card">
                <span className="shop-products-stat-card__label">Categories</span>
                <strong className="shop-products-stat-card__value">{uniqueCategories.length}</strong>
              </div>
            </div>
          </div>
        </section>

        <section className="shop-products-toolbar">
          <div className="shop-products-toolbar__summary">
            <span className="shop-products-toolbar__label">Filter</span>
            <strong>{activeFilterLabel}</strong>
          </div>

          <div className="shop-products-search-wrap">
            <span className="shop-products-search-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search products..."
              className={`shop-products-search-input ${searchQuery ? "has-value" : ""}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="shop-products-search-clear"
                onClick={() => setSearchQuery("")}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>

          <div className="shop-products-filters">
            {categoryChips.map((chip) => (
              <button
                key={chip.value}
                type="button"
                className={`shop-products-filter-chip ${filterCategory === chip.value ? "active" : ""}`}
                onClick={() => setFilterCategory(chip.value)}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </section>

        {loading ? (
          <div className="shop-products-state shop-products-state--loading">
            <div className="shop-products-spinner"></div>
            <p>Loading products...</p>
          </div>
        ) : error ? (
          <div className="shop-products-state shop-products-state--error">
            <h3>Unable to load products</h3>
            <p>{error}</p>
            <button type="button" className="shop-products-secondary-btn" onClick={goBack}>
              Go Back
            </button>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="shop-products-empty">
            <div className="shop-products-empty__icon">
              <svg width="54" height="54" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                <line x1="12" y1="22.08" x2="12" y2="12"></line>
              </svg>
            </div>
            <h3>No products found</h3>
            <p>
              {searchQuery.trim()
                ? `No products match your search for "${searchQuery}".`
                : filterCategory === "all"
                ? "This shop has not listed any products yet. Check back later for new arrivals."
                : `There are no products in ${activeFilterLabel.toLowerCase()} right now.`}
            </p>
            <div className="shop-products-empty__actions">
              {searchQuery.trim() && (
                <button
                  type="button"
                  className="shop-products-primary-btn"
                  onClick={() => setSearchQuery("")}
                >
                  Clear Search
                </button>
              )}
              {filterCategory !== "all" && !searchQuery.trim() && (
                <button
                  type="button"
                  className="shop-products-primary-btn"
                  onClick={() => setFilterCategory("all")}
                >
                  View All Products
                </button>
              )}
              <button type="button" className="shop-products-secondary-btn" onClick={goBack}>
                Back to Shops
              </button>
            </div>
          </div>
        ) : (
          <section className="shop-products-grid">
            {filteredProducts.map((p) => (
              <article key={p.vsp_id} className="shop-product-card">
                <div 
                  className="shop-product-card__image-wrap"
                  onClick={() => setSelectedProduct(p)}
                  style={{ cursor: "pointer" }}
                >
                  {p.vsp_image ? (
                    <img
                      src={`${imageBaseUrl}/${p.vsp_image}`}
                      alt={p.vsp_name}
                      className="shop-product-card__image"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        e.currentTarget.parentElement.classList.add("is-fallback");
                      }}
                    />
                  ) : null}
                  <div className="shop-product-card__image-fallback">No Image</div>
                </div>
                <div className="shop-product-card__content">
                  <div 
                    onClick={() => setSelectedProduct(p)}
                    style={{ cursor: "pointer", display: "flex", flexDirection: "column", gap: "10px", flexGrow: 1 }}
                  >
                    <div className="shop-product-card__top">
                      <span className="shop-product-card__category">Category {p.vsp_category}</span>
                      {(p.vsp_point > 0) && (
                        <span style={{
                          display: "inline-block",
                          marginLeft: "8px",
                          padding: "2px 10px",
                          borderRadius: "20px",
                          background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                          color: "#fff",
                          fontWeight: "700",
                          fontSize: "0.78rem",
                          letterSpacing: "0.02em"
                        }}>
                          🏆 {p.vsp_point} pts
                        </span>
                      )}
                    </div>
                    <h3 className="shop-product-card__title">{p.vsp_name}</h3>
                    <p className="shop-product-card__description">{p.vsp_description}</p>
                    {Number(p.vsp_save_point) > 0 && (
                      <p className="shop-product-card__description" style={{ marginBottom: "0.9rem", color: "#059669", fontWeight: 700 }}>
                        Save Point: {p.vsp_save_point} pts
                      </p>
                    )}
                  </div>
                  <button 
                    type="button" 
                    className="shop-products-primary-btn" 
                    disabled={cartItems.some(i => i.vsp_id === p.vsp_id)}
                    style={{ 
                      marginTop: "12px", width: "100%", padding: "10px", 
                      borderRadius: "12px", border: "none", cursor: cartItems.some(i => i.vsp_id === p.vsp_id) ? "not-allowed" : "pointer", 
                      background: cartItems.some(i => i.vsp_id === p.vsp_id) ? "rgba(148, 163, 184, 0.2)" : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", 
                      color: cartItems.some(i => i.vsp_id === p.vsp_id) ? "#8ca3be" : "white", 
                      fontWeight: "bold" 
                    }}
                    onClick={() => handleAddToCart(p)}
                  >
                    {cartItems.some(i => i.vsp_id === p.vsp_id) ? "Added to Cart" : "Add to Cart"}
                  </button>
                </div>
              </article>
            ))}
          </section>
        )}
      </div>

      {selectedProduct && (
        <div className="shop-product-modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="shop-product-modal-container" onClick={(e) => e.stopPropagation()}>
            <button 
              className="shop-product-modal-close" 
              onClick={() => setSelectedProduct(null)}
              aria-label="Close modal"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div className="shop-product-modal-body">
              <div className="shop-product-modal-image-wrap">
                {selectedProduct.vsp_image ? (
                  <img
                    src={`${imageBaseUrl}/${selectedProduct.vsp_image}`}
                    alt={selectedProduct.vsp_name}
                    className="shop-product-modal-image"
                  />
                ) : (
                  <div className="shop-product-modal-image-fallback">No Image</div>
                )}
              </div>
              <div className="shop-product-modal-details">
                <div className="shop-product-modal-tags">
                  <span className="shop-product-modal-category">Category {selectedProduct.vsp_category}</span>
                  {selectedProduct.vsp_point > 0 && (
                    <span className="shop-product-modal-points">🏆 {selectedProduct.vsp_point} pts</span>
                  )}
                </div>
                <h2 className="shop-product-modal-title">{selectedProduct.vsp_name}</h2>
                <div className="shop-product-modal-divider"></div>
                <p className="shop-product-modal-description">{selectedProduct.vsp_description}</p>
                {Number(selectedProduct.vsp_save_point) > 0 && (
                  <div className="shop-product-modal-save-point">
                    Save Point: <span>{selectedProduct.vsp_save_point} pts</span>
                  </div>
                )}
                <div className="shop-product-modal-actions">
                  <button 
                    type="button" 
                    className="shop-products-primary-btn shop-product-modal-btn" 
                    disabled={cartItems.some(i => i.vsp_id === selectedProduct.vsp_id)}
                    onClick={() => {
                      handleAddToCart(selectedProduct);
                    }}
                  >
                    {cartItems.some(i => i.vsp_id === selectedProduct.vsp_id) ? "Added to Cart" : "Add to Cart"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopProducts;
